import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProductTour.css';

const TOUR_KEY = 'trace_tour_completed_v1';

/**
 * Tour steps. Each step either:
 *  - targets a [data-tour="..."] element (spotlight + tooltip), or
 *  - is "centered" (welcome / finish modal with no target).
 *
 * `route` (optional) navigates before the step shows, so the highlighted
 * area is actually on screen.
 */
const STEPS = [
  {
    id: 'welcome',
    centered: true,
    title: 'Welcome to TRACE',
    body: "Your floor is set up. Let's take 60 seconds to walk through where everything lives so nothing gets lost.",
  },
  {
    id: 'dashboard',
    target: 'dashboard',
    route: '/dashboard',
    title: 'Dashboard',
    body: 'Your command center. A live snapshot of what is running, what is queued, and what needs attention right now.',
  },
  {
    id: 'queue',
    target: 'queue',
    title: 'Live Job Queue',
    body: 'Every active job in real time. Drag to reorder priority and update status from any device on the floor.',
  },
  {
    id: 'jobs',
    target: 'jobs',
    title: 'Jobs',
    body: 'The full record of every job — searchable history, statuses, and the QR label for each one.',
  },
  {
    id: 'inventory',
    target: 'inventory',
    title: 'Inventory',
    body: 'Track stock alongside your jobs so you always know what materials are on hand.',
  },
  {
    id: 'departments',
    target: 'departments',
    title: 'Departments',
    body: 'The departments you just created live here. Add, rename, or reassign them any time.',
  },
  {
    id: 'locations',
    target: 'locations',
    title: 'Locations',
    body: 'Your physical spots on the floor — lanes, shelves, and stations a job can move between via QR scan.',
  },
  {
    id: 'floor-audit',
    target: 'floor-audit',
    title: 'Floor Audit',
    body: 'Run a 5-minute sweep of the floor to confirm every job is exactly where the system says it is.',
  },
  {
    id: 'audit-log',
    target: 'audit-log',
    title: 'Audit Log',
    body: 'A complete, time-stamped trail of every scan and move — who did what, and when.',
  },
  {
    id: 'settings',
    target: 'settings',
    title: 'Settings & Team',
    body: 'Manage your organization, kiosk mode, billing, and — most importantly — invite your team so they can start scanning.',
  },
  {
    id: 'finish',
    centered: true,
    title: "You're ready to run.",
    body: 'That is the whole floor. Create your first job and watch it move through TRACE in real time.',
    cta: { label: 'Create your first job', route: '/jobs?new=1' },
  },
];

const PADDING = 8; // spotlight breathing room around target

export function hasSeenTour() {
  try {
    return localStorage.getItem(TOUR_KEY) === '1';
  } catch {
    return false;
  }
}

export function markTourSeen() {
  try {
    localStorage.setItem(TOUR_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function resetTour() {
  try {
    localStorage.removeItem(TOUR_KEY);
  } catch {
    /* ignore */
  }
}

export default function ProductTour({ open, onClose, accentColor }) {
  const navigate = useNavigate();
  const accent = accentColor || '#3b82f6'; // TRACE Blue fallback

  // Pick black or white button text depending on how light the accent is,
  // so e.g. a white accent still has readable button labels.
  const accentInk = (() => {
    const hex = accent.replace('#', '');
    if (hex.length < 6) return '#07111f';
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#07111f' : '#ffffff';
  })();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, placement: 'right' });
  const tooltipRef = useRef(null);

  const step = STEPS[index];
  const isFirst = index === 0;
  const isLast = index === STEPS.length - 1;

  // Reset to the first step whenever the tour is (re)opened.
  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  // Navigate if a step asks for a particular route.
  useEffect(() => {
    if (open && step?.route) navigate(step.route);
  }, [open, index]); // eslint-disable-line react-hooks/exhaustive-deps

  const measure = useCallback(() => {
    if (!step || step.centered) {
      setRect(null);
      return;
    }
    document
      .querySelectorAll('.tour-target-active')
      .forEach((n) => n.classList.remove('tour-target-active'));

    const el = document.querySelector(`[data-tour="${step.target}"]`);
    if (!el) {
      setRect(null);
      return;
    }
    el.classList.add('tour-target-active');
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top - PADDING,
      left: r.left - PADDING,
      width: r.width + PADDING * 2,
      height: r.height + PADDING * 2,
      cx: r.left + r.width / 2,
      cy: r.top + r.height / 2,
    });
  }, [step]);

  // Measure after paint, and again after a tick in case the route just changed.
  useLayoutEffect(() => {
    if (!open) return;
    measure();
    const t = setTimeout(measure, 60);
    return () => clearTimeout(t);
  }, [open, index, measure]);

  // Keep aligned on resize / scroll.
  useEffect(() => {
    if (!open) return;
    const handler = () => measure();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [open, measure]);

  // Position the tooltip relative to the spotlight (or center it).
  useLayoutEffect(() => {
    if (!open) return;
    const tip = tooltipRef.current;
    const tw = tip?.offsetWidth ?? 320;
    const th = tip?.offsetHeight ?? 180;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (!rect) {
      setTooltipPos({
        top: vh / 2 - th / 2,
        left: vw / 2 - tw / 2,
        placement: 'center',
      });
      return;
    }

    // Sidebar items sit on the left, so default to placing the tooltip to the right.
    let placement = 'right';
    let left = rect.left + rect.width + 16;
    let top = rect.cy - th / 2;

    if (left + tw > vw - 16) {
      placement = 'left';
      left = rect.left - tw - 16;
    }
    if (left < 16) {
      placement = 'bottom';
      left = Math.min(Math.max(16, rect.cx - tw / 2), vw - tw - 16);
      top = rect.top + rect.height + 16;
    }
    top = Math.min(Math.max(16, top), vh - th - 16);
    setTooltipPos({ top, left, placement });
  }, [rect, open, index]);

  const finish = useCallback(() => {
    document
      .querySelectorAll('.tour-target-active')
      .forEach((n) => n.classList.remove('tour-target-active'));
    markTourSeen();
    onClose?.();
  }, [onClose]);

  const next = useCallback(() => {
    if (isLast) finish();
    else setIndex((i) => i + 1);
  }, [isLast, finish]);

  const prev = useCallback(() => {
    if (!isFirst) setIndex((i) => i - 1);
  }, [isFirst]);

  // Keyboard: Esc skips, arrows navigate.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, next, prev, finish]);

  if (!open || !step) return null;

  const handleCta = () => {
    finish();
    if (step.cta?.route) navigate(step.cta.route);
  };

  return (
    <div
      className="tour-root"
      role="dialog"
      aria-modal="true"
      aria-label="Product tour"
      style={{
        '--tour-accent': accent,
        '--tour-accent-soft': `${accent}2e`, // ~18% alpha
        '--tour-accent-glow': `${accent}59`, // ~35% alpha
        '--tour-accent-ink': accentInk,
      }}
    >
      {/* Dimmed overlay with a cut-out spotlight via SVG mask */}
      <svg className="tour-overlay" width="100%" height="100%">
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left}
                y={rect.top}
                width={rect.width}
                height={rect.height}
                rx="10"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(8, 10, 14, 0.78)"
          mask="url(#tour-mask)"
        />
      </svg>

      {/* Glowing ring around the spotlight */}
      {rect && (
        <div
          className="tour-spotlight-ring"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        />
      )}

      {/* Tooltip / step card */}
      <div
        ref={tooltipRef}
        className={`tour-tooltip placement-${tooltipPos.placement}`}
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
      >
        <div className="tour-tooltip-head">
          <span className="tour-eyebrow">
            // step {index + 1} of {STEPS.length}
          </span>
          <button className="tour-skip" onClick={finish} aria-label="Skip tour">
            Skip ✕
          </button>
        </div>

        <h3 className="tour-title">{step.title}</h3>
        <p className="tour-body">{step.body}</p>

        <div className="tour-progress">
          {STEPS.map((s, i) => (
            <span
              key={s.id}
              className={`tour-dot ${i === index ? 'current' : ''} ${i < index ? 'done' : ''}`}
            />
          ))}
        </div>

        <div className="tour-actions">
          <button
            className="tour-btn ghost"
            onClick={prev}
            disabled={isFirst}
            style={{ visibility: isFirst ? 'hidden' : 'visible' }}
          >
            Back
          </button>

          {step.cta ? (
            <button className="tour-btn primary" onClick={handleCta}>
              {step.cta.label} →
            </button>
          ) : (
            <button className="tour-btn primary" onClick={next}>
              {isLast ? 'Done' : 'Next →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}